import { Button } from "@/components/flexkit/Button";
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { TrackLocations } from "@/hooks/use-segment";
import { useDeleteWebhook, useWebhookWorkflowsWithRunningStatus, type PlainWebhook } from "@/queries/webhook-queries";
import { formatError } from "@/utils/errors";
import { useCallback, type FC } from "react";
import { useNavigate } from "react-router-dom";
import { webhooksUrl, workflowName } from "@/utils/automations";
import { IconTriangleExclamation } from "central-icons/IconTriangleExclamation";

type DeleteWebhookDialogProps = {
    webhook: PlainWebhook;
    open: boolean;
    onClose: () => void;
};

export const DeleteWebhookDialog: FC<DeleteWebhookDialogProps> = ({ webhook, open, onClose }) => {
    const { toast } = useToast();
    const navigate = useNavigate();
    const deleteWebhook = useDeleteWebhook();

    const { workflows, isLoading, hasRunningExecutions, hasBoundWorkflows } = useWebhookWorkflowsWithRunningStatus(
        webhook.id,
        open,
    );

    const webhookName = webhook.metadata?.name || "Unnamed webhook";

    const handleDelete = useCallback(async () => {
        try {
            await deleteWebhook.mutateAsync(webhook.id);
            toast({
                title: "Webhook deleted",
                description: `Webhook has been deleted successfully.`,
            });
            onClose();
            navigate(webhooksUrl());
        } catch (error) {
            toast({
                title: "Failed to delete webhook",
                description: formatError(error),
            });
        }
    }, [deleteWebhook, webhook.id, toast, onClose, navigate]);

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent data-testid="delete-webhook-dialog" data-track-location={TrackLocations.DeleteWebhookDialog}>
                <DialogHeader className="mb-0">
                    <DialogTitle>
                        {hasBoundWorkflows ? "Delete webhook" : <>Delete webhook &apos;{webhookName}&apos;</>}
                    </DialogTitle>
                    <DialogDescription>
                        This removes the webhook from Ona. It will not delete the webhook in GitHub or GitLab.
                    </DialogDescription>
                </DialogHeader>
                {hasBoundWorkflows && (
                    <DialogBody>
                        <div className="rounded-lg border border-border-warning px-4 py-3 text-content-primary">
                            <div className="flex gap-3">
                                <IconTriangleExclamation size={16} className="mt-0.5 shrink-0 text-content-warning" />
                                <div className="flex flex-col gap-3">
                                    <p>
                                        Deleting this webhook will switch these automations to <strong>Manual</strong>{" "}
                                        and stop future PR-triggered runs.
                                    </p>
                                    {hasRunningExecutions && (
                                        <p>
                                            Any automation that is currently executing will continue until it completes.
                                        </p>
                                    )}
                                    <div>
                                        <p className="font-medium">The following automations will be affected:</p>
                                        <ul className="mt-1 list-inside list-disc">
                                            {workflows.map(({ workflow, isRunning }) => (
                                                <li key={workflow.id}>
                                                    {workflowName(workflow)}
                                                    {isRunning && (
                                                        <span className="ml-1 italic text-content-secondary">
                                                            - Execution running
                                                        </span>
                                                    )}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </DialogBody>
                )}
                <DialogFooter className="justify-end">
                    <DialogClose asChild>
                        <Button variant="outline" data-testid="delete-webhook-dialog-cancel">
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        variant="destructive"
                        onClick={handleDelete}
                        loading={deleteWebhook.isPending || isLoading}
                        disabled={isLoading}
                        data-testid="delete-webhook-dialog-confirm"
                        data-tracking-id="confirm-delete-webhook-delete-webhook-dialog"
                    >
                        Delete webhook
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
